import { useQuery } from '@tanstack/react-query'
import { axiosInstance } from '../helpers'
import { normalizeListResponse } from '../helpers/companyDocumentList'
import { OrganizationContractTypeCoverage } from '../models/Company/types'

const QUERY_ROOT = ['organizationContractTypeCoverage'] as const

export interface OrganizationContractTypeCoverageParams {
    organization: number | null
    includeInactive?: boolean
    contractTypeId?: number | null
    enabled?: boolean
}

const buildCoverageQueryString = (
    params: OrganizationContractTypeCoverageParams,
) => {
    if (!params.organization) return ''
    const searchParams = new URLSearchParams()
    searchParams.append('organization', String(params.organization))
    if (params.includeInactive) {
        searchParams.append('include_inactive', 'true')
    }
    if (params.contractTypeId != null) {
        searchParams.append('contract_type', String(params.contractTypeId))
    }
    const qs = searchParams.toString()
    return qs ? `?${qs}` : ''
}

export const useOrganizationContractTypeCoverage = (
    params: OrganizationContractTypeCoverageParams,
) => {
    const queryString = buildCoverageQueryString(params)

    const { data: coverage, isLoading, error, refetch } = useQuery<
        OrganizationContractTypeCoverage[],
        Error
    >({
        queryKey: [
            ...QUERY_ROOT,
            params.organization,
            params.includeInactive,
            params.contractTypeId,
        ],
        queryFn: async () => {
            const res = await axiosInstance.get<unknown>(
                `/company/organization_contract_type/coverage/${queryString}`,
            )
            return normalizeListResponse<OrganizationContractTypeCoverage>(
                res.data,
            )
        },
        enabled: !!params.organization && params.enabled !== false,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    return {
        coverage: coverage ?? [],
        isLoading,
        error,
        refetch,
    }
}
